import { useState, useEffect, useCallback } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { seguimientoApi } from '../api/seguimiento'
import logoLigier from '../assets/logo_ligier.png'

// Paleta de marca Ligier — blanco y negro
const INK = '#111111'      // negro de marca
const INK_SOFT = '#000000' // negro puro (header)

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function iniciales(nombre) {
  if (!nombre) return '🚐'
  return nombre.trim().split(/\s+/).slice(0, 2).map(p => p[0]).join('').toUpperCase()
}

// El backend serializa en UTC; mostramos en hora argentina con zona FIJA (no la
// del navegador del cliente, que puede estar en otra zona) para que vea la hora
// real de la entrega.
const TZ_AR = 'America/Argentina/Buenos_Aires'
const FMT_DIA = new Intl.DateTimeFormat('es-AR', { timeZone: TZ_AR, weekday: 'short', day: '2-digit', month: '2-digit' })
const FMT_DIA_HORA = new Intl.DateTimeFormat('es-AR', { timeZone: TZ_AR, day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })

// Una fecha PELADA (YYYY-MM-DD, sin hora) es una fecha de negocio: se muestra
// tal cual, sin pasar por zonas horarias. `new Date('2026-09-02')` la
// interpreta como medianoche UTC y en hora argentina retrocede al día
// anterior — la promesa de entrega del 02/09 se mostraba "lun 01/09"
// (incidente 31 ago 2026; misma regla que utils/fecha.js).
const SOLO_FECHA = /^\d{4}-\d{2}-\d{2}$/
const FMT_DIA_PELADA = new Intl.DateTimeFormat('es-AR', { timeZone: 'UTC', weekday: 'short', day: '2-digit', month: '2-digit' })

function fmtFecha(f) {
  if (!f) return null
  const s = String(f)
  if (SOLO_FECHA.test(s)) return FMT_DIA_PELADA.format(new Date(`${s}T00:00:00Z`))
  const d = new Date(s)
  if (isNaN(d)) return s
  return FMT_DIA.format(d)
}

function fmtFechaHora(f) {
  if (!f) return null
  const s = String(f)
  if (SOLO_FECHA.test(s)) return fmtFecha(s)
  const d = new Date(s)
  if (isNaN(d)) return s
  return FMT_DIA_HORA.format(d)
}

// ---------------------------------------------------------------------------
// Bottom sheets
// ---------------------------------------------------------------------------

// Tocar afuera NO cierra: adentro hay formularios (reprogramar, dejar
// instrucciones) y perder lo que el cliente escribió es peor que un toque de
// más. Se cierra con el botón de la hoja.
function Sheet({ title, children, onClose }) {
  return (
    <div style={s.overlay}>
      <div style={s.sheet}>
        <h2 style={s.sheetTitle}>{title}</h2>
        {children}
      </div>
    </div>
  )
}

function ReprogramarSheet({ token, onClose, onDone }) {
  const [fecha, setFecha] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState('')
  const submit = async (e) => {
    e.preventDefault(); setEnviando(true); setError('')
    try { await seguimientoApi.reprogramar(token, { nueva_fecha: fecha }); onDone(fecha) }
    catch (err) { setError(err.response?.data?.message || 'No se pudo reprogramar.'); setEnviando(false) }
  }
  return (
    <Sheet title="📅 Reprogramar entrega" onClose={onClose}>
      {error && <div style={s.err}>{error}</div>}
      <form onSubmit={submit}>
        <label style={s.lbl}>Elegí el nuevo día</label>
        <input type="date" value={fecha} onChange={e => setFecha(e.target.value)} required style={s.inp} />
        <div style={s.sheetBtns}>
          <button type="button" onClick={onClose} style={s.btnGhost}>Cancelar</button>
          <button type="submit" disabled={enviando || !fecha} style={s.btnPrimary}>{enviando ? 'Guardando…' : 'Confirmar'}</button>
        </div>
      </form>
    </Sheet>
  )
}

function InstruccionesSheet({ token, actual, onClose, onDone }) {
  const [txt, setTxt] = useState(actual || '')
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState('')
  const submit = async (e) => {
    e.preventDefault(); setEnviando(true); setError('')
    try { await seguimientoApi.instrucciones(token, { instrucciones: txt }); onDone() }
    catch (err) { setError(err.response?.data?.message || 'No se pudo guardar.'); setEnviando(false) }
  }
  return (
    <Sheet title="🛎️ Instrucciones de entrega" onClose={onClose}>
      {error && <div style={s.err}>{error}</div>}
      <form onSubmit={submit}>
        <label style={s.lbl}>¿Algo que el repartidor deba saber?</label>
        <textarea data-nocaps value={txt} onChange={e => setTxt(e.target.value)} maxLength={500} style={{ ...s.inp, height: 90, resize: 'vertical' }}
          placeholder="Ej: Tocar timbre 3°B. Si no atiendo, llamar al portero." />
        <div style={s.sheetBtns}>
          <button type="button" onClick={onClose} style={s.btnGhost}>Cancelar</button>
          <button type="submit" disabled={enviando} style={s.btnPrimary}>{enviando ? 'Guardando…' : 'Guardar'}</button>
        </div>
      </form>
    </Sheet>
  )
}

// El reclamo del cliente (Martin, 16 ago 2026): atajar el enojo antes de la
// reseña. Va directo al vendedor de la venta y al depósito como aviso
// emergente en LGR — por eso la confirmación promete contacto, no un ticket.
function ReclamoSheet({ token, onClose }) {
  const [txt, setTxt] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [listo, setListo] = useState(false)
  const [error, setError] = useState('')
  const submit = async (e) => {
    e.preventDefault(); setEnviando(true); setError('')
    try { await seguimientoApi.reclamo(token, { mensaje: txt }); setListo(true) }
    catch (err) { setError(err.response?.data?.message || 'No se pudo enviar. Probá de nuevo.'); setEnviando(false) }
  }
  if (listo) {
    return (
      <Sheet title="✅ Recibimos tu reclamo" onClose={onClose}>
        <p style={{ margin: '0 0 14px', color: '#374151', lineHeight: 1.5 }}>
          Tu vendedor y nuestro depósito ya fueron avisados. Te contactamos a la
          brevedad para resolverlo.
        </p>
        <div style={s.sheetBtns}>
          <button type="button" onClick={onClose} style={{ ...s.btnPrimary, width: '100%' }}>Cerrar</button>
        </div>
      </Sheet>
    )
  }
  return (
    <Sheet title="⚠️ Iniciar un reclamo" onClose={onClose}>
      {error && <div style={s.err}>{error}</div>}
      <form onSubmit={submit}>
        <label style={s.lbl}>Contanos qué pasó</label>
        <textarea data-nocaps value={txt} onChange={e => setTxt(e.target.value)} maxLength={1000} required
          style={{ ...s.inp, height: 110, resize: 'vertical' }}
          placeholder="Ej: llegó una botella rota / el pedido no llega y nadie me contactó…" />
        <div style={s.sheetBtns}>
          <button type="button" onClick={onClose} style={s.btnGhost}>Cancelar</button>
          <button type="submit" disabled={enviando || txt.trim().length < 5} style={s.btnPrimary}>{enviando ? 'Enviando…' : 'Enviar reclamo'}</button>
        </div>
      </form>
    </Sheet>
  )
}

/** Estrellitas reutilizables (general + repartidor). */
function Estrellas({ valor, onChange, size = 30 }) {
  const [hover, setHover] = useState(0)
  return (
    <div style={s.stars}>
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} type="button" onClick={() => onChange(n)} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
          style={{ ...s.star, fontSize: size, color: n <= (hover || valor) ? INK : '#d1d5db' }} aria-label={`${n} estrellas`}>★</button>
      ))}
    </div>
  )
}

/** Sí / No de la encuesta de entrega. */
function SiNo({ label, valor, onChange }) {
  return (
    <div style={s.siNoRow}>
      <span style={s.siNoLblRow}>{label}</span>
      <div style={{ display: 'flex', gap: 6 }}>
        {[['Sí', true], ['No', false]].map(([txt, v]) => (
          <button key={txt} type="button" onClick={() => onChange(v)}
            style={{ ...s.siNoBtn, ...(valor === v ? s.siNoBtnOn : {}) }}>{txt}</button>
        ))}
      </div>
    </div>
  )
}

/**
 * Encuesta de conformidad de la ENTREGA (Martin, 9 ago 2026): la estrella
 * general abre 3 preguntas cortas + comentario. Un toque cada una.
 */
function RatingBlock({ token, onDone }) {
  const [general, setGeneral] = useState(0)
  const [aTiempo, setATiempo] = useState(null)
  const [buenEstado, setBuenEstado] = useState(null)
  const [repartidor, setRepartidor] = useState(0)
  const [comentario, setComentario] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [listo, setListo] = useState(false)
  const [falta, setFalta] = useState('')

  const enviar = async () => {
    if (!general) return
    if (aTiempo === null || buenEstado === null) { setFalta('Contestá las dos preguntas de Sí/No — es un toque.'); return }
    setEnviando(true)
    try {
      await seguimientoApi.encuesta(token, {
        general,
        a_tiempo: aTiempo,
        buen_estado: buenEstado,
        repartidor: repartidor || null,
        comentario: comentario || null,
      })
      setListo(true); onDone?.()
    } catch {
      // Nunca fallar en silencio: en el celular "no pasó nada" se lee como roto.
      setFalta('No se pudo enviar. Probá de nuevo en un momento.')
      setEnviando(false)
    }
  }

  if (listo) return <p style={{ textAlign: 'center', color: '#166534', fontWeight: 700, margin: '8px 0' }}>¡Gracias por tu opinión! 🙌</p>

  return (
    <div style={s.ratingWrap}>
      <p style={s.ratingTitle}>¿Cómo estuvo la entrega?</p>
      <Estrellas valor={general} onChange={setGeneral} />
      {general > 0 && (
        <>
          <SiNo label="¿Llegó cuando lo esperabas?" valor={aTiempo} onChange={setATiempo} />
          <SiNo label="¿Llegó todo en buen estado?" valor={buenEstado} onChange={setBuenEstado} />
          <div style={{ marginTop: 10 }}>
            <span style={s.siNoLbl}>¿Y la atención del repartidor?</span>
            <Estrellas valor={repartidor} onChange={setRepartidor} size={24} />
          </div>
          <textarea data-nocaps value={comentario} onChange={e => setComentario(e.target.value)} maxLength={1000}
            style={{ ...s.inp, height: 64, resize: 'vertical', marginTop: 10 }} placeholder="Contanos más (opcional)" />
          {falta && <p style={{ color: '#991b1b', fontSize: 13, margin: '8px 0 0' }}>{falta}</p>}
          <button onClick={enviar} disabled={enviando} style={{ ...s.btnPrimary, width: '100%', marginTop: 10 }}>
            {enviando ? 'Enviando…' : 'Enviar opinión'}
          </button>
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Página principal
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Pedido T80 con varios envíos: el pedido va a MÁS de una dirección — una
// card por envío, cada una con link a su seguimiento completo (donde viven
// reprogramar, instrucciones, encuesta y reclamo de ESE envío).
// ---------------------------------------------------------------------------

function estadoEnvio(e) {
  if (e.estado === 'entregado') return { icono: '✓', label: e.retiro ? 'Retirado' : 'Entregado' }
  if (e.estado === 'listo_para_retirar') return { icono: '🏬', label: 'Listo para retirar' }
  if (e.estado === 'no_entregado') return { icono: '✕', label: 'No pudimos entregar' }
  if (e.estado === 'reagendado') return { icono: '↻', label: 'Entrega reprogramada' }
  if (e.estado === 'pendiente' && e.salida_despachada) return { icono: '🚚', label: 'En camino' }
  return { icono: '📦', label: 'En preparación' }
}

function PedidoMultiple({ d }) {
  // El título acompaña la realidad: todos entregados → celebrarlo; alguno
  // en la calle → "en camino"; si no, la promesa de los N envíos.
  const entregados = d.envios.filter(e => e.estado === 'entregado').length
  const enCamino = d.envios.some(e => e.estado === 'pendiente' && e.salida_despachada)
  const titulo = entregados === d.envios.length ? '¡Pedido entregado!'
    : enCamino ? 'Tu pedido está en camino'
    : `Tu pedido se entrega en ${d.envios.length} envíos`
  const subtitulo = entregados === d.envios.length
    ? `Los ${d.envios.length} envíos fueron entregados`
    : (entregados > 0 ? `${entregados} de ${d.envios.length} envíos entregados` : null)

  return (
    <div style={s.wrap}>
      <div style={s.header}>
        <img src={logoLigier} alt="Ligier" style={s.logo} />
        <h1 style={s.title}>{titulo}</h1>
        <p style={s.numero}>Pedido {d.numero}{subtitulo ? ` · ${subtitulo}` : ''}</p>
      </div>
      <div style={s.body}>
        {d.envios.map((e, i) => {
          const t = estadoEnvio(e)
          const entregado = e.estado === 'entregado'
          return (
            <div key={e.token || i} style={s.card}>
              <p style={s.cardLbl}>Envío {i + 1} de {d.envios.length}</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                <span style={{ ...s.multiIcono, background: entregado ? INK : '#f4f4f5', color: entregado ? '#fff' : INK }}>{t.icono}</span>
                <div>
                  <p style={{ margin: 0, fontWeight: 800, color: INK, fontSize: '1.05rem' }}>{t.label}</p>
                  {e.destinatario && <p style={{ margin: 0, fontSize: '0.9rem', color: '#3b3b3b' }}>Recibe {e.destinatario}</p>}
                </div>
              </div>
              <p style={s.info}>📍 {[e.direccion, e.localidad].filter(Boolean).join(', ') || 'Dirección a confirmar'}</p>
              {!entregado && e.ventana?.fecha && <p style={s.info}>🗓 Entrega estimada: {fmtFecha(e.ventana.fecha)}</p>}
              {entregado && e.entrega?.entregado_at && (
                <p style={s.info}>✓ Entregado el {fmtFechaHora(e.entrega.entregado_at)}{e.entrega.receptor_nombre ? ` — recibió ${e.entrega.receptor_nombre}` : ''}</p>
              )}
              {e.token && (
                <a href={`/seguimiento/${e.token}`} style={s.multiLink}>Seguir este envío →</a>
              )}
            </div>
          )
        })}
        <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: '0.8rem', marginTop: 8, padding: '0 8px' }}>
          Cada envío tiene su propio seguimiento: tocá uno para ver el detalle,
          reprogramarlo o dejar indicaciones de entrega.
        </p>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// "¿Querés ser cliente de Ligier?" — los T80 son obsequiados de nuestros
// clientes: no tenemos sus datos. Dejan el mail acá y a las 48 h les llega
// la invitación a crear su cuenta en la tienda (leads:invitar-obsequiados).
// ---------------------------------------------------------------------------

function ContactoLigier({ token, registrado }) {
  const [email, setEmail] = useState('')
  const [estado, setEstado] = useState(registrado ? 'listo' : 'form') // form | enviando | listo | error

  const enviar = async (e) => {
    e.preventDefault()
    setEstado('enviando')
    try {
      await seguimientoApi.contacto(token, { email: email.trim() })
      setEstado('listo')
    } catch {
      setEstado('error')
    }
  }

  if (estado === 'listo') {
    return (
      <div style={s.card}>
        <p style={s.cardLbl}>🍷 Vinoteca Ligier</p>
        <p style={s.info}>
          <strong>¡Gracias!</strong> En unos días te va a llegar una invitación
          para crear tu cuenta en <strong>ligier.com.ar</strong>.
        </p>
      </div>
    )
  }

  return (
    <div style={s.card}>
      <p style={s.cardLbl}>🍷 ¿Querés ser cliente de Ligier?</p>
      <p style={s.info}>
        Este envío salió de nuestra vinoteca. Dejanos tu mail y te mandamos una
        invitación para crear tu cuenta y conocer la tienda.
      </p>
      {/* Dos renglones (Martin, 31 ago 2026): el mail arriba, "Quiero" abajo. */}
      <form onSubmit={enviar} style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
        <input
          type="email" required placeholder="tu@email.com" value={email}
          onChange={e => setEmail(e.target.value)}
          style={{ width: '100%', boxSizing: 'border-box', padding: '12px', border: '1px solid #d4d4d4', borderRadius: 10, fontSize: '0.95rem' }}
        />
        <button type="submit" disabled={estado === 'enviando' || !email.trim()} style={{ ...s.btnPrimary, width: '100%' }}>
          {estado === 'enviando' ? 'Enviando…' : 'Quiero'}
        </button>
      </form>
      {estado === 'error' && (
        <p style={{ margin: '8px 0 0', fontSize: '0.8rem', color: '#b91c1c' }}>
          No pudimos guardar tu mail — probá de nuevo en un rato.
        </p>
      )}
    </div>
  )
}

export default function SeguimientoClientePage() {
  const { token } = useParams()
  const [sp] = useSearchParams()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  // ?reclamo=1 (viene del botón del portal) abre el reclamo directo.
  const [sheet, setSheet] = useState(sp.get('reclamo') === '1' ? 'reclamo' : null) // 'reprogramar' | 'instrucciones' | 'reclamo'
  // Confirmación de una acción recién hecha (la fecha nueva ya se ve en la
  // card, pero el cliente tiene que leer que quedó hecho).
  const [confirmacion, setConfirmacion] = useState('')

  const cargar = useCallback(async () => {
    try {
      const res = await seguimientoApi.obtener(token)
      setData(res.data)
    } catch {
      setError('No encontramos tu pedido. Revisá el enlace.')
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => { cargar() }, [cargar])

  if (loading) return <div style={s.full}><p style={{ color: '#9ca3af' }}>Cargando tu pedido…</p></div>
  if (error) return (
    <div style={s.full}>
      <div style={{ textAlign: 'center' }}>
        <p style={{ fontSize: '2.5rem', marginBottom: 8 }}>📦</p>
        <p style={{ color: INK, fontWeight: 700 }}>{error}</p>
      </div>
    </div>
  )

  const d = data
  // T80 con varios envíos → una card por envío, con link al seguimiento de cada uno.
  if (d.multiple) return <PedidoMultiple d={d} />
  const entregado = d.estado === 'entregado'
  const noEntregado = d.estado === 'no_entregado'
  const reagendado = d.estado === 'reagendado'
  const enCamino = d.estado === 'pendiente' && d.salida_despachada
  // Retiro por sucursal (sug #89): sin viaje, sin dirección de entrega ni
  // reprogramación — la sucursal avisa cuando está listo y el cliente lo busca.
  const retiro = d.retiro
  const listoRetirar = d.estado === 'listo_para_retirar'

  const titulo = retiro
    ? (entregado ? '¡Pedido retirado!' : listoRetirar ? 'Tu pedido está listo para retirar' : 'Estamos preparando tu pedido')
    : entregado ? '¡Pedido entregado!'
    : noEntregado ? 'No pudimos entregar'
    : reagendado ? 'Entrega reprogramada'
    : enCamino ? 'Tu pedido está en camino'
    : d.andreani ? 'Tu pedido viaja por Andreani'
    : 'Estamos preparando tu pedido'

  // "09:00:00" es un dato de base, no algo que se le muestre a una persona:
  // 9 · 14:30. Mismo criterio que el remito impreso.
  const hhmm = (h) => {
    if (!h) return null
    const [hs, ms] = String(h).split(':')
    return Number(ms) === 0 ? String(Number(hs)) : `${Number(hs)}:${ms}`
  }
  const desde = hhmm(d.ventana?.hora_desde)
  const hasta = hhmm(d.ventana?.hora_hasta)
  // El remito trae el horario escrito a mano; la NE, dos horas.
  const ventana = d.ventana?.horario
    ? d.ventana.horario
    : desde && hasta ? `de ${desde} a ${hasta}`
    : desde ? `desde las ${desde}`
    : hasta ? `hasta las ${hasta}`
    : null

  return (
    <div style={s.wrap}>
      {sheet === 'reprogramar' && <ReprogramarSheet token={token} onClose={() => setSheet(null)} onDone={(fecha) => { setSheet(null); setConfirmacion(`Listo: tu entrega quedó para el ${fmtFecha(fecha)}.`); cargar() }} />}
      {sheet === 'instrucciones' && <InstruccionesSheet token={token} actual={d.instrucciones_cli} onClose={() => setSheet(null)} onDone={() => { setSheet(null); cargar() }} />}
      {sheet === 'reclamo' && <ReclamoSheet token={token} onClose={() => setSheet(null)} />}

      {/* Header */}
      <div style={s.header}>
        <img src={logoLigier} alt="Ligier" style={s.logo} />
        <h1 style={s.title}>{titulo}</h1>
        <p style={s.numero}>Pedido {d.numero}</p>
      </div>

      <div style={s.body}>
        {confirmacion && (
          <div style={s.ok} role="status">✓ {confirmacion}</div>
        )}

        {/* Entregado: foto + receptor + rating */}
        {entregado && (
          <div style={s.card}>
            <div style={{ textAlign: 'center', marginBottom: 10 }}>
              <span style={s.bigCheck}>✓</span>
            </div>
            <p style={s.entregadoTxt}>
              {retiro ? 'Retirado' : 'Entregado'}{d.entrega?.entregado_at ? ` el ${fmtFechaHora(d.entrega.entregado_at)}` : ''}
              {d.entrega?.receptor_nombre ? ` · ${retiro ? 'Retiró' : 'Recibió'}: ${d.entrega.receptor_nombre}` : ''}
            </p>
            {d.entrega?.foto && (
              <img src={d.entrega.foto} alt="Foto de la entrega" style={s.fotoEntrega} />
            )}
            {!d.rating && <RatingBlock token={token} onDone={cargar} />}
            {d.rating && <p style={{ textAlign: 'center', color: INK, fontSize: '1.3rem', margin: '8px 0' }}>{'★'.repeat(d.rating)}<span style={{ color: '#d1d5db' }}>{'★'.repeat(5 - d.rating)}</span></p>}
          </div>
        )}

        {/* No entregado */}
        {noEntregado && (
          <div style={{ ...s.card, borderLeft: `4px solid #dc2626` }}>
            <p style={{ margin: 0, color: '#991b1b', fontWeight: 700 }}>No pudimos entregar tu pedido.</p>
            {d.entrega?.motivo && <p style={{ margin: '6px 0 0', color: '#555' }}>Motivo: {d.entrega.motivo}</p>}
            {/* Siniestro del transporte: no hay entrega que mover; lo resuelve
                el vendedor contactando al cliente. */}
            {d.siniestro ? (
              <p style={{ margin: '10px 0 0', color: '#555', fontSize: '0.9rem' }}>En breve nos vamos a comunicar con vos para resolverlo.</p>
            ) : (
              <>
                <p style={{ margin: '10px 0 0', color: '#555', fontSize: '0.9rem' }}>Reprogramá la entrega cuando quieras, vos elegís el día.</p>
                <button onClick={() => setSheet('reprogramar')} style={{ ...s.btnPrimary, width: '100%', marginTop: 12 }}>📅 Reprogramar entrega</button>
              </>
            )}
          </div>
        )}

        {/* Retiro por sucursal: dónde y en qué horario (sug #89). */}
        {retiro && !entregado && (
          <div style={{ ...s.card, borderLeft: `4px solid ${INK}` }}>
            <p style={s.cardLbl}>{listoRetirar ? 'Podés pasar a buscarlo por' : 'Lo vas a retirar por'}</p>
            {retiro.sucursal ? (
              <>
                <p style={{ margin: '4px 0 0', color: INK, fontWeight: 700 }}>{retiro.sucursal.nombre}</p>
                {retiro.sucursal.direccion && <p style={s.info}>📍 {retiro.sucursal.direccion}</p>}
                {retiro.sucursal.horario && <p style={s.info}>🕐 {retiro.sucursal.horario}</p>}
              </>
            ) : <p style={s.info}>la sucursal que elegiste.</p>}
            {!listoRetirar && <p style={{ margin: '8px 0 0', color: '#555', fontSize: '0.9rem' }}>Te avisamos por mail cuando esté listo.</p>}
          </div>
        )}

        {/* Reagendado */}
        {reagendado && (
          <div style={{ ...s.card, borderLeft: `4px solid ${INK}` }}>
            <p style={{ margin: 0, color: INK_SOFT, fontWeight: 700 }}>
              Entrega reprogramada{d.entrega?.nueva_fecha ? ` para el ${fmtFecha(d.entrega.nueva_fecha)}` : ''}.
            </p>
            <p style={{ margin: '8px 0 0', color: '#555', fontSize: '0.9rem' }}>Te avisamos cuando vuelva a salir. 🚐</p>
          </div>
        )}

        {/* Envío por Andreani: tracking + link al seguimiento del correo.
            Visible también entregado (el cliente puede querer ver el detalle). */}
        {d.andreani && !noEntregado && (
          <div style={s.card}>
            <p style={s.cardLbl}>Tu pedido viaja por Andreani</p>
            <p style={{ margin: '4px 0 0', color: '#555', fontSize: '0.92rem' }}>
              Número de seguimiento:{' '}
              <strong style={{ fontFamily: 'ui-monospace, monospace', userSelect: 'all' }}>{d.andreani.tracking}</strong>
            </p>
            {d.andreani.estado && !entregado && (
              <p style={{ margin: '6px 0 0', color: '#555', fontSize: '0.9rem' }}>Último estado: {d.andreani.estado}</p>
            )}
            <a
              href={d.andreani.url} target="_blank" rel="noopener noreferrer"
              style={{ ...s.btnPrimary, display: 'block', textAlign: 'center', marginTop: 12, textDecoration: 'none', boxSizing: 'border-box' }}
            >📦 Seguir mi envío en Andreani</a>
          </div>
        )}

        {/* "Viaja con Juan" — solo mientras está activo */}
        {!entregado && !noEntregado && d.fletero && (
          <div style={s.card}>
            <p style={s.cardLbl}>Tu pedido viaja con</p>
            <div style={s.fleteroRow}>
              {d.fletero.foto_url
                ? <img src={d.fletero.foto_url} alt={d.fletero.nombre} style={s.fleteroFoto} />
                : <div style={s.fleteroAvatar}>{iniciales(d.fletero.nombre)}</div>}
              <div style={{ minWidth: 0 }}>
                <p style={s.fleteroNombre}>{d.fletero.nombre}</p>
                {d.fletero.vehiculo && <p style={s.fleteroVeh}>{d.fletero.vehiculo}</p>}
              </div>
            </div>
          </div>
        )}

        {/* Timeline */}
        {!noEntregado && !reagendado && (
          <div style={s.card}>
            <div style={s.timeline}>
              {d.timeline?.map((p, i) => (
                <div key={p.key} style={s.tlStep}>
                  <div style={{ ...s.tlDot, ...(p.done ? s.tlDotDone : {}), ...(p.current ? s.tlDotCurrent : {}) }}>
                    {p.done ? '✓' : i + 1}
                  </div>
                  <span style={{ ...s.tlLabel, color: p.current ? INK : (p.done ? '#444' : '#bbb'), fontWeight: p.current ? 800 : 600 }}>{p.label}</span>
                  {i < d.timeline.length - 1 && <div style={{ ...s.tlLine, background: p.done ? INK : '#e5e5e5' }} />}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Datos del pedido */}
        <div style={s.card}>
          {d.es_regalo && d.dedicatoria && (
            <div style={s.regalo}>🎁 {d.dedicatoria}</div>
          )}
          {!retiro && d.direccion && <p style={s.info}>📍 {[d.direccion, d.localidad].filter(Boolean).join(', ')}</p>}
          {!retiro && ventana && <p style={s.info}>🕐 Ventana estimada: {ventana}</p>}
          {!retiro && d.ventana?.fecha && <p style={s.info}>📆 {fmtFecha(d.ventana.fecha)}</p>}
          {d.articulos?.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <p style={s.cardLbl}>Tu pedido</p>
              <div style={s.arts}>
                {d.articulos.map((a, i) => (
                  <div key={i} style={s.artChip}><span>{a.descripcion}</span><strong style={{ marginLeft: 6 }}>×{a.cantidad}</strong></div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Acciones self-service — solo cuando todavía no llegó */}
        {!retiro && !entregado && !noEntregado && !reagendado && (
          <div style={s.actions}>
            <p style={s.cardLbl}>¿Necesitás algo?</p>
            <button onClick={() => setSheet('reprogramar')} style={s.btnAction}>📅 Reprogramar entrega</button>
            <button onClick={() => setSheet('instrucciones')} style={s.btnAction}>
              🛎️ {d.instrucciones_cli ? 'Editar instrucciones' : 'Dejar instrucciones de entrega'}
            </button>
          </div>
        )}

        {/* Reclamo: SOLO con la entrega ya cerrada — entregada, no entregada o
            reagendada (regla de Martin, 28 ago 2026: un reclamo abierto con el
            pedido todavía en camino no le llega a quien puede resolverlo).
            Antes se preguntaba `!enCamino`, y eso lo mostraba también con el
            pedido esperando en el depósito —el caso que se vio en el remito
            1700008023, en `confirmado`, sin despachar— porque `enCamino` exige
            que la salida YA haya salido. Mientras el pedido no llegó, lo que
            el cliente tiene son las instrucciones de entrega, acá arriba. */}
        {(entregado || noEntregado || reagendado) && (
          <div style={{ textAlign: 'center', marginTop: 18 }}>
            <button onClick={() => setSheet('reclamo')} style={s.btnReclamo}>
              ⚠️ ¿Tuviste un problema? Iniciá un reclamo
            </button>
          </div>
        )}

        {/* Obsequiado de un T80: dejá tu mail y te invitamos a ser cliente. */}
        {d.contacto?.permitido && <ContactoLigier token={token} registrado={d.contacto.registrado} />}

        <p style={s.legal}>
          Gracias por elegir Ligier. La venta de bebidas alcohólicas está prohibida a menores de 18 años (Ley 24.788).
        </p>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Estilos
// ---------------------------------------------------------------------------

const s = {
  full: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#ffffff', padding: 24 },
  wrap: { minHeight: '100vh', background: '#ffffff', maxWidth: 480, margin: '0 auto', paddingBottom: 32, fontFamily: '-apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif' },

  header: { background: INK_SOFT, color: '#fff', padding: '24px 20px 28px', borderBottomLeftRadius: 22, borderBottomRightRadius: 22 },
  logo: { height: 76, width: 'auto', display: 'block', marginBottom: 16 },
  title: { margin: '0 0 2px', fontSize: '1.45rem', fontWeight: 800, lineHeight: 1.2 },
  numero: { margin: 0, fontSize: '0.9rem', opacity: 0.75 },

  body: { padding: '16px 14px 0' },
  // Pedido con varios envíos
  multiIcono: { width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: '1.1rem', flexShrink: 0, border: '1px solid #e5e5e5' },
  multiLink: { display: 'block', marginTop: 10, textAlign: 'center', background: INK, color: '#fff', borderRadius: 12, padding: '12px 14px', fontWeight: 800, textDecoration: 'none', fontSize: '0.95rem' },
  card: { background: '#fff', borderRadius: 16, padding: 16, marginBottom: 12, border: '1px solid #ececec', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' },
  cardLbl: { margin: '0 0 8px', fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.06em', color: '#9ca3af', textTransform: 'uppercase' },
  info: { margin: '6px 0', fontSize: '0.95rem', color: '#3b3b3b', lineHeight: 1.4 },

  // Fletero
  fleteroRow: { display: 'flex', alignItems: 'center', gap: 12 },
  fleteroFoto: { width: 56, height: 56, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: `2px solid ${INK}` },
  fleteroAvatar: { width: 56, height: 56, borderRadius: '50%', background: INK, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '1.1rem', flexShrink: 0 },
  fleteroNombre: { margin: 0, fontSize: '1.15rem', fontWeight: 800, color: '#111' },
  fleteroVeh: { margin: '2px 0 0', fontSize: '0.85rem', color: '#9ca3af' },

  // Timeline
  timeline: { display: 'flex', justifyContent: 'space-between', position: 'relative' },
  tlStep: { display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, position: 'relative' },
  tlDot: { width: 32, height: 32, borderRadius: '50%', background: '#e5e5e5', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.85rem', zIndex: 2 },
  tlDotDone: { background: INK },
  tlDotCurrent: { background: INK, boxShadow: `0 0 0 4px #d4d4d4` },
  tlLabel: { marginTop: 6, fontSize: '0.78rem', textAlign: 'center' },
  tlLine: { position: 'absolute', top: 16, left: '50%', width: '100%', height: 3, zIndex: 1 },

  // Entregado
  bigCheck: { display: 'inline-flex', width: 64, height: 64, borderRadius: '50%', background: INK, color: '#fff', alignItems: 'center', justifyContent: 'center', fontSize: '2rem', fontWeight: 900 },
  entregadoTxt: { textAlign: 'center', margin: '0 0 12px', color: '#3b3b3b', fontSize: '0.95rem' },
  fotoEntrega: { width: '100%', borderRadius: 12, display: 'block', marginBottom: 12 },

  // Regalo
  regalo: { background: '#f4f4f5', border: '1px solid #e5e5e5', borderRadius: 12, padding: '10px 12px', color: '#333', fontSize: '0.95rem', marginBottom: 10, fontStyle: 'italic' },

  // Artículos
  arts: { display: 'flex', flexDirection: 'column', gap: 6 },
  artChip: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f4f4f5', borderRadius: 10, padding: '8px 12px', fontSize: '0.9rem', color: '#3b3b3b' },

  // Acciones
  actions: { background: '#fff', borderRadius: 16, padding: 16, marginBottom: 12, border: '1px solid #ececec', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' },
  btnAction: { width: '100%', padding: '14px', marginTop: 8, background: '#f4f4f5', border: `1.5px solid #e5e5e5`, borderRadius: 12, color: INK, fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer', textAlign: 'left' },
  btnReclamo: { padding: '12px 18px', background: '#fff', border: '1.5px solid #d4d4d4', borderRadius: 12, color: INK, fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer' },

  // Rating
  ratingWrap: { marginTop: 8, borderTop: '1px solid #eee', paddingTop: 14 },
  ratingTitle: { textAlign: 'center', margin: '0 0 8px', fontWeight: 700, color: '#333' },
  stars: { display: 'flex', justifyContent: 'center', gap: 6 },
  star: { background: 'none', border: 'none', fontSize: '2rem', cursor: 'pointer', lineHeight: 1, padding: 0 },
  siNoRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginTop: 12 },
  siNoLblRow: { fontSize: '0.9rem', color: '#333', fontWeight: 600, flex: 1, textAlign: 'left' },
  siNoLbl: { fontSize: '0.9rem', color: '#333', fontWeight: 600, display: 'block', textAlign: 'center', marginBottom: 6 },
  siNoBtn: { border: '1px solid #d1d5db', background: '#fff', color: '#333', borderRadius: 8, padding: '7px 18px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer' },
  siNoBtnOn: { background: '#111', borderColor: '#111', color: '#fff' },

  legal: { textAlign: 'center', fontSize: '0.72rem', color: '#9ca3af', margin: '18px 8px 0', lineHeight: 1.5 },

  // Sheets
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', zIndex: 100 },
  sheet: { background: '#fff', borderRadius: '20px 20px 0 0', padding: '22px 18px calc(22px + env(safe-area-inset-bottom))', width: '100%', maxWidth: 480, margin: '0 auto', boxSizing: 'border-box' },
  sheetTitle: { fontSize: '1.1rem', fontWeight: 800, margin: '0 0 16px', color: '#111' },
  sheetBtns: { display: 'flex', gap: 10, marginTop: 16 },
  lbl: { display: 'block', fontSize: '0.88rem', fontWeight: 600, color: '#555', marginBottom: 6 },
  inp: { width: '100%', padding: '12px', borderRadius: 10, border: '1px solid #e0e0e0', fontSize: '1rem', boxSizing: 'border-box' },
  err: { background: '#fee2e2', color: '#991b1b', padding: 10, borderRadius: 8, marginBottom: 12, fontSize: '0.9rem' },
  ok: { background: '#111', color: '#fff', padding: '10px 14px', borderRadius: 10, marginBottom: 12, fontSize: '0.95rem', fontWeight: 600 },
  btnPrimary: { flex: 2, padding: '14px', borderRadius: 12, background: INK, color: '#fff', border: 'none', fontWeight: 700, fontSize: '1rem', cursor: 'pointer' },
  btnGhost: { flex: 1, padding: '14px', borderRadius: 12, background: '#f1f1f1', color: '#555', border: 'none', fontWeight: 700, fontSize: '1rem', cursor: 'pointer' },
}
